import { useEffect, useMemo, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent } from "./ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { Loader2, Search, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { buildFreeSkipTracePack, type SkipTracePack } from "../lib/skipTrace";
import { apiJson } from "../lib/api";

function openNewTab(url: string) {
  window.open(url, "_blank", "noopener,noreferrer");
}

type FreeSkipTraceDialogProps = {
  address: string;
  defaultOwnerName?: string;
  buttonLabel?: string;
};

export function FreeSkipTraceDialog({
  address,
  defaultOwnerName = "",
  buttonLabel = "Free Skip Trace",
}: FreeSkipTraceDialogProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pack, setPack] = useState<SkipTracePack | null>(null);
  const [ownerName, setOwnerName] = useState(defaultOwnerName);

  useEffect(() => {
    if (open && !ownerName && defaultOwnerName) {
      setOwnerName(defaultOwnerName);
    }
  }, [open, defaultOwnerName, ownerName]);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setLoading(true);

    apiJson<{ success?: boolean; data?: any; error?: string }>("/api/skip-trace", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ address, ownerName }),
    })
      .then((json) => {
        if (!json.success) {
          throw new Error(json.error || "Failed to load free skip trace workflow");
        }
        if (!cancelled) {
          setPack(json.data);
        }
      })
      .catch((error) => {
        console.error(error);
        toast.error("Could not load free skip trace workflow");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, address]);

  const workflow = useMemo(() => {
    if (!pack) return null;
    return buildFreeSkipTracePack({
      address: pack.address,
      county: pack.county,
      city: pack.city,
      state: pack.state,
      ownerName,
    });
  }, [pack, ownerName]);

  const handleCopy = async (text: string) => {
    await navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        className="h-7 px-2 ml-auto"
        onClick={() => setOpen(true)}
      >
        <Search className="w-3 h-3 mr-1" /> {buttonLabel}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[calc(100dvh-1rem)] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Free Skip Trace Workflow</DialogTitle>
            <DialogDescription>
              Use public records and free search tools instead of a paid skip-tracing API.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid gap-2">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Property</p>
                  <p className="font-medium">{address}</p>
                </div>
                {pack?.locationHint ? <Badge variant="secondary">{pack.locationHint}</Badge> : null}
              </div>
              <div className="grid gap-2">
                <label className="text-sm font-medium">Owner name from assessor record</label>
                <Input
                  value={ownerName}
                  onChange={(e) => setOwnerName(e.target.value)}
                  placeholder={defaultOwnerName || "Enter owner name after checking county records"}
                />
              </div>
              <p className="text-sm text-muted-foreground">
                Start with the assessor record, then use the owner name in the free people-search tools below.
              </p>
            </div>

            {loading ? (
              <div className="flex items-center gap-2 rounded-lg border p-4 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading public-record search links...
              </div>
            ) : null}

            {workflow ? (
              <div className="grid gap-4 md:grid-cols-2">
                <Card className="md:col-span-2">
                  <CardContent className="p-4 space-y-3">
                    <h4 className="font-semibold">Manual steps</h4>
                    <ol className="space-y-2 text-sm text-slate-700 list-decimal list-inside">
                      {workflow.steps.map((step) => (
                        <li key={step}>{step}</li>
                      ))}
                    </ol>
                    <div className="flex flex-wrap gap-2 pt-1">
                      <Button variant="outline" size="sm" onClick={() => handleCopy(workflow.steps.join("\n"))}>
                        Copy steps
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleCopy(workflow.address)}>
                        Copy address
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {workflow.resources.map((resource) => (
                  <Card key={resource.label}>
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h4 className="font-semibold">{resource.label}</h4>
                          <p className="text-sm text-muted-foreground">{resource.description}</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" onClick={() => openNewTab(resource.href)}>
                          <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleCopy(resource.href)}>
                          Copy link
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
